import { useState } from 'react';
import { useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  SelectField,
  type Column,
} from '@eon/ui';
import { hooks, type OmSessionListItem } from '@eon/api-client';
import { formatDate, formatDuration, formatKWh } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { useSessionStatusFilterOptions } from '@/lib/session-status-filter';
import { SessionDetailDialog } from '@/components/portal/SessionDetailDialog';
import { SessionStatusBadge } from '@/components/status/StatusBadges';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function SessionsPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const statusOptions = useSessionStatusFilterOptions();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState<string>('__all__');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useMySessions({
    page,
    pageSize,
    status: status === '__all__' ? undefined : status,
    from: from || undefined,
    to: to || undefined,
    ...sortParams,
  });

  const columns: Column<OmSessionListItem>[] = [
    { key: 'station', header: t('portal.sessions.station'), cell: (s) => s.stationName ?? '—' },
    { key: 'device', header: t('portal.sessions.device'), cell: (s) => s.deviceDisplayName ?? '—' },
    { key: 'user', header: t('portal.sessions.user'), cell: (s) => s.userName ?? '—' },
    { key: 'connector', header: t('device.connectors'), sortable: true, sortKey: 'connector', sortValue: (s) => s.connectorId, cell: (s) => `#${s.connectorId}` },
    { key: 'energy', header: t('session.energy'), sortable: true, sortValue: (s) => s.totalEnergyKWh, cell: (s) => formatKWh(s.totalEnergyKWh) },
    { key: 'duration', header: t('session.duration'), sortable: true, sortValue: (s) => s.durationSeconds, cell: (s) => formatDuration(s.durationSeconds) },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      cell: (s) => <SessionStatusBadge status={String(s.status)} />,
    },
  ];

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title={t('nav.sessions')}
        description={list.data ? `${totalCount.toLocaleString()} ${t('nav.sessions')}` : undefined}
      />

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end">
        <SelectField
          className="w-full lg:w-44"
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={statusOptions}
          placeholder={t('common.status')}
        />
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs">{t('portal.sessions.dateFrom')}</label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </div>
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs">{t('portal.sessions.dateTo')}</label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </div>
        <SelectField
          className="w-full lg:w-36"
          value={String(pageSize)}
          onValueChange={(v) => {
            setPageSize(Number(v));
            setPage(1);
          }}
          options={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
          placeholder={t('pagination.rowsPerPage')}
        />
      </div>

      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(s) => s.id}
        onRowClick={(s) => setSelectedId(s.id)}
        emptyTitle={t('common.noData')}
        sort={sort}
        onSortChange={onSortChange}
      />

      {list.data && totalCount > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}

      <SessionDetailDialog
        sessionId={selectedId}
        open={Boolean(selectedId)}
        onOpenChange={(o) => !o && setSelectedId(null)}
      />
    </>
  );
}
